import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";
import { APPLICATIONS } from "@/data/applications";
import { MATERIALS, MATERIAL_CATEGORIES } from "@/data/materials";
import { PRODUCTS } from "@/data/products";
import { Reveal, MaskedLine, SectionHead, SpecChip } from "@/components/Reveal";

const MARQUEE_ITEMS = [
  "INCONEL X-750", "INCONEL 718", "HASTELLOY C-276", "MONEL 400", "MONEL K-500", "17-7PH", "17-4PH",
  "SS 302 / 304 / 316", "SAE 9254 CHROME SILICON", "50CRV4", "SUP9 / SUP11", "EN47", "MUSIC WIRE ASTM A228", "BERYLLIUM COPPER", "PHOSPHOR BRONZE",
];

const CountUp = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1600;
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref} className="font-display text-6xl lg:text-7xl text-white leading-none">
      {val.toLocaleString()}
      <span className="text-[#9C9C9C]">{suffix}</span>
    </span>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMAGES.heroFactory} alt="MESCO Springs manufacturing plant" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FEFEFE] via-[#FEFEFE]/75 to-white/25" />
        <div className="absolute inset-0 blueprint-grid opacity-60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono-spec text-[11px] sm:text-xs tracking-[0.35em] text-[#334772] uppercase mb-6"
          data-testid="hero-spec-label"
        >
          [ ISO 9001:2015 CERTIFIED · EST. 1969 ]
        </motion.p>
        <h1 className="font-display uppercase leading-[0.88] tracking-tight text-[#334772]" data-testid="hero-headline">
          <MaskedLine delay={0.35} className="text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">Absorb.</MaskedLine>
          <MaskedLine delay={0.5} className="text-[17vw] sm:text-[13vw] lg:text-[9.5rem] text-[#706F70]">Restore.</MaskedLine>
          <MaskedLine delay={0.65} className="text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">Energize.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#706F70] leading-relaxed"
        >
          Heavy duty and precision springs engineered in Mumbai since 1969 — from 0.1&nbsp;mm instrument wire to 40&nbsp;mm
          hot-wound crusher springs, in everything from music wire to Inconel, Hastelloy, Monel and 17-7PH.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/enquiry"
            data-testid="hero-rfq-btn"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#334772] text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors duration-200"
          >
            Send an RFQ
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a
            href={SITE.mainSite}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-mainsite-btn"
            className="group inline-flex items-center gap-3 px-8 py-4 border border-[#334772]/40 text-[#334772] text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#334772] hover:text-white transition-colors duration-200"
          >
            mescosprings.com
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="relative z-10 border-t border-[#334772]/15 bg-white/80 backdrop-blur-sm overflow-hidden"
        data-testid="materials-marquee"
      >
        <div className="flex whitespace-nowrap animate-marquee py-4">
          {[0, 1].map((n) => (
            <div key={n} className="flex shrink-0">
              {MARQUEE_ITEMS.map((m) => (
                <span key={`${n}-${m}`} className="font-mono-spec text-xs tracking-[0.3em] text-[#9C9C9C] uppercase px-6 flex items-center gap-6">
                  {m} <span className="text-[#334772]">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const Stats = () => (
  <section className="border-b border-[#334772]/15 bg-[#334772]" data-testid="stats-band">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
      {[
        { to: 55, suffix: "+", label: "Years of spring engineering" },
        { to: 5000, suffix: "+", label: "Satisfied customers" },
        { to: 2000, suffix: "+", label: "Tonnes of springs per year" },
        { to: 135, suffix: "+", label: "Employees in Mumbai" },
      ].map((s) => (
        <Reveal key={s.label}>
          <CountUp to={s.to} suffix={s.suffix} />
          <p className="mt-3 font-mono-spec text-[11px] tracking-[0.2em] uppercase text-[#9C9C9C]">{s.label}</p>        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => {
  const chapters = [
    { n: "01", t: "Absorb", d: "Shock loads, impact energy, vibration. Our springs take the hit so your machine doesn't — from vibrating screens in iron ore mines to buffer springs at the bottom of lift shafts." },
    { n: "02", t: "Restore", d: "Equilibrium is engineering. Pipe hangers carrying kilometres of hot piping, safety valves holding set pressure for a decade, breakers that must trip in milliseconds — our springs return everything to centre." },
    { n: "03", t: "Energize", d: "Stored energy on demand. Circuit breaker closing springs, actuator springs, recoil systems — wound, preset and certified to release exactly the energy your design calculated." },
  ];
  return (
    <section className="py-24 lg:py-32" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHead index="—" label="The Manifesto" title="What a spring actually does" />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#334772]/10 border border-[#334772]/15">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} className="bg-[#FEFEFE]">
              <div className="crosshair-card p-10 h-full group hover:bg-[#FFFFFF] transition-colors duration-300">
                <span className="font-display text-6xl text-[#334772]/25 group-hover:text-[#334772]/60 transition-colors duration-300">{c.n}</span>
                <h3 className="font-display text-4xl text-[#334772] uppercase tracking-wide mt-4">{c.t}</h3>
                <p className="mt-4 text-sm text-[#706F70] leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const MaterialsSpotlight = () => (
  <section className="py-24 lg:py-32 bg-[#F3F5F8] border-y border-[#334772]/15 relative overflow-hidden" data-testid="materials-spotlight">
    <div className="absolute inset-0 blueprint-grid opacity-40" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHead index="01" label="Exotic Raw Materials" title="We make what others can't" />
          <Reveal delay={0.15}>
            <p className="mt-6 text-[#706F70] leading-relaxed">
              Most spring makers stop at carbon steel and stainless. MESCO stocks and coils the exotic alloys that
              extreme service demands — high temperature, sour gas, seawater, chlorides. If your drawing calls for
              Inconel, Hastelloy, Monel or precipitation-hardening stainless, you have found one of the few factories
              in India that will quote it without flinching.
            </p>
            <Link
              to="/materials"
              data-testid="materials-explore-link"
              className="group mt-8 inline-flex items-center gap-3 text-sm font-bold uppercase tracking-[0.15em] text-[#334772] hover:text-[#706F70] transition-colors duration-200"
            >
              All spring materials
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#334772]/10 border border-[#334772]/15">
          {MATERIALS.slice(0, 4).map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.08}>
              <Link
                to={`/materials/${m.slug}`}
                data-testid={`material-card-${m.slug}`}
                className="block bg-[#FFFFFF] p-8 h-full group hover:-translate-y-1 hover:bg-[#F3F5F8] transition-all duration-300 border border-transparent hover:border-[#334772]"
              >
                <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">{m.category}</span>
                <h3 className="font-display text-3xl text-[#334772] uppercase tracking-wide mt-2 group-hover:text-[#706F70] transition-colors duration-200">
                  {m.name}
                </h3>
                <p className="mt-3 text-sm text-[#706F70] leading-relaxed line-clamp-3">{m.tagline}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {m.properties.slice(1, 3).map((p) => (
                    <SpecChip key={p.label}>{p.value}</SpecChip>
                  ))}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.1} className="mt-8">
        <div className="flex flex-wrap gap-2">
          {MATERIAL_CATEGORIES.map((c) => (
            <SpecChip key={c}>{c}</SpecChip>
          ))}
          {MATERIALS.slice(4).map((m) => (
            <Link key={m.slug} to={`/materials/${m.slug}`} data-testid={`material-chip-${m.slug}`}>
              <span className="inline-block font-mono-spec text-[10px] tracking-[0.2em] uppercase text-[#706F70] border border-[#334772]/25 px-3 py-1.5 hover:border-[#334772] hover:text-[#334772] transition-colors duration-200">
                {m.name}
              </span>
            </Link>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const ApplicationsGrid = () => (
  <section className="py-24 lg:py-32" data-testid="applications-grid">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <SectionHead index="02" label="Industries Served" title="Springs for every battlefield" />
        <Reveal delay={0.1}>
          <Link
            to="/applications"
            data-testid="applications-all-link"
            className="group inline-flex items-center gap-3 text-sm font-bold uppercase tracking-[0.15em] text-[#334772] hover:text-[#706F70] transition-colors duration-200"
          >
            All applications
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {APPLICATIONS.map((a, i) => (
          <Reveal key={a.slug} delay={(i % 3) * 0.1} className={i % 5 === 0 ? "md:col-span-2" : ""}>
            <Link
              to={`/applications/${a.slug}`}
              data-testid={`application-card-${a.slug}`}
              className="group relative block h-72 overflow-hidden border border-[#334772]/15 hover:border-[#334772] transition-colors duration-300"
            >
              <img
                src={a.image}
                alt={a.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6">
                <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-white">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-2xl lg:text-3xl text-white uppercase tracking-wide mt-1">{a.nav}</h3>
                <p className="mt-1 text-xs text-[#DDE3EE] opacity-0 group-hover:opacity-100 transition-opacity duration-300">{a.tagline}</p>
              </div>
              <ArrowUpRight className="absolute top-5 right-5 w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProductsStrip = () => (
  <section className="py-24 lg:py-32 bg-[#F3F5F8] border-y border-[#334772]/15" data-testid="products-strip">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead index="03" label="The Product Range" title="One roof. Every spring." />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#334772]/10 border border-[#334772]/15">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 0.08}>
            <Link
              to={`/products/${p.slug}`}
              data-testid={`product-card-${p.slug}`}
              className="group block bg-white p-8 h-full hover:bg-[#F3F5F8] hover:-translate-y-1 transition-all duration-300"
            >
              <span className="font-mono-spec text-[10px] tracking-[0.25em] text-[#9C9C9C]">{String(i + 1).padStart(2, "0")} / PRODUCT</span>
              <h3 className="font-display text-3xl text-[#334772] uppercase tracking-wide mt-3 group-hover:text-[#706F70] transition-colors duration-200">
                {p.nav}
              </h3>
              <p className="mt-3 text-sm text-[#706F70] leading-relaxed">{p.tagline}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-mono-spec text-[10px] tracking-[0.2em] uppercase text-[#334772] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                View range <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const VideoSection = () => (
  <section className="py-24 lg:py-32" data-testid="video-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <SectionHead index="04" label="Inside the Works" title="Watch the machines coil" />
        <Reveal delay={0.15}>
          <p className="mt-6 text-[#706F70] leading-relaxed">
            Five-axis CNC coilers, heavy hot-winding lines, in-house heat treatment, Magnaflux crack detection and
            digital load testing to 20&nbsp;MT — two minutes inside the MESCO works in Andheri, Mumbai.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <SpecChip>5-AXIS CNC COILING</SpecChip>
            <SpecChip>HOT WINDING TO 40 MM</SpecChip>
            <SpecChip>LOAD TEST TO 20 MT</SpecChip>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="crosshair-card border border-[#334772]/15 bg-[#FFFFFF] p-2">
          <div className="relative aspect-video">
            <iframe
              src={SITE.youtubeEmbed}
              title="MESCO Springs Corporate Video"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="corporate-video"
            />
          </div>
          <div className="flex items-center justify-between px-3 py-2">
            <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C] flex items-center gap-2">
              <Play className="w-3 h-3 text-[#334772]" /> Corporate film · 02:21
            </span>
            <a href={SITE.youtube} target="_blank" rel="noopener noreferrer" className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] hover:text-[#334772] transition-colors" data-testid="youtube-link">
              youtube.com/@mescosprings
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CTABand = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="cta-band">
    <div className="absolute inset-0 bg-[#334772]" />
    <div className="absolute inset-0 blueprint-grid opacity-30" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <p className="font-mono-spec text-[11px] tracking-[0.35em] uppercase text-[#9C9C9C]">[ RFQ RESPONSE WITHIN 24 HOURS ]</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] text-white mt-6">
          Have a drawing?<br />We have the wire.
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-[#DDE3EE] leading-relaxed">
          Send your specification to India's exotic-material spring specialists. Or continue to our main website for
          the complete company profile, client list and plant tour.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/enquiry" data-testid="cta-rfq-btn" className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-[#334772] text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#F3F5F8] transition-colors duration-200">
            Send an RFQ <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="cta-mainsite-btn" className="group inline-flex items-center gap-3 px-8 py-4 border border-white/40 text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#334772] transition-colors duration-200">
            Visit mescosprings.com <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Helmet>
        <title>MESCO Springs | Heavy Duty & Precision Spring Manufacturer India | Inconel, Hastelloy, Monel Springs</title>
        <meta
          name="description"
          content="MESCO Springs — ISO 9001:2015 spring manufacturer since 1969. Heavy duty & precision springs 0.1–40mm wire in Inconel, Hastelloy, Monel, 17-7PH, stainless & alloy steels. Mining, elevator, valve, oil & gas springs. Mumbai, India. Export worldwide."
        />
        <meta name="keywords" content="spring manufacturer india, heavy duty springs, inconel springs, hastelloy springs, monel springs, 17-7ph springs, compression springs, elevator buffer springs, crusher springs, valve springs, mesco springs" />
        <link rel="canonical" href="https://mescospring.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MESCO Springs",
          alternateName: "Modern Engineering and Spring Company",
          url: "https://www.mescosprings.com",
          email: "marketing@mescosprings.com",
          telephone: "+91 85549 85323",
          foundingDate: "1969",
          address: {
            "@type": "PostalAddress",
            streetAddress: "11, Sona Udyog Estate, Parsi Panchayat Road, Andheri East",
            addressLocality: "Mumbai",
            postalCode: "400069",
            addressRegion: "Maharashtra",
            addressCountry: "IN",
          },
          sameAs: ["https://www.mescosprings.com", "https://mescosprings.in", "https://youtube.com/@mescosprings", "https://www.linkedin.com/company/mescosprings"],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "MESCO Springs",
          url: "https://mescospring.com/",
        })}</script>
      </Helmet>
      <Hero />
      <Stats />
      <Manifesto />
      <MaterialsSpotlight />
      <ApplicationsGrid />
      <ProductsStrip />
      <VideoSection />
      <CTABand />
    </>
  );
}
